/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/

import APP_CONSTANTS, { publicConstants } from '../constants';
import { createC11nEnv } from '../interpreter/c11n-env';
import {
  getActiveContainerItemContext,
  getChildContainers,
  getActiveContainerItemName,
  getParentContainerItem
} from '../container/container-utils';
import type { Action } from '../store/state/state-manager';
import { getDataObject } from '../store/state/state-manager';
import RouteParser from '../router/route-parser';
import type { Actionout } from '../types';
import { isParentAndChildContextHoldingSameItem } from '../case/common-helper';

import PubSubUtils from './pubsub-utils';

type GlobalObject = typeof globalThis & {
  uniqueId?: number;
};
export interface AssignmentParameters extends Action {
  payload: {
    data: { caseInfo: { content: { coexistenceAction: string }; assignments: { ID: string }[] } };
    isParallelAssignment?: boolean;
  };
}
class BroadcastUtils {
  public broadcastChannel: BroadcastChannel | null;
  public callback: Function | null;
  public isReverseCoexistenceCase: boolean;
  public broadcastEventToIframe: boolean;
  public isDirtyDialogActive: boolean;
  public onCreateCallback: Function | null;
  public openAssignmentParams: Actionout[] | null;
  public static readonly OPEN_ASSIGNMENT_ACTION: string = 'openAssignment';
  public static readonly DIRTY_POPUP_C11N: string = 'displayDirtyPopupFromConstellation';
  public static readonly HANDLE_INFINITY_DISCARD: string = 'handleDiscardInInfinity';
  public static readonly OPEN_ASSIGNMENT_MODAL: string = 'openAssignmentInModal';
  public static readonly DISPLAY_DIRTY_POPUP: string = 'displayDirtyPopup';
  public static readonly LOAD_PREVIEW: string = 'loadPreview';
  public static readonly UPDATE_COEXISTENCE_DRILLDOWN_CASE: string = 'updateCoexistenceDrilldownCase';
  public static readonly OPEN_WORK_ITEM: string = 'openWorkItem';
  public static readonly OPEN_WORK_BY_HANDLE: string = 'openWorkByHandle';
  public static readonly GET_NEXT_WORK_ACTION: string = 'getNextWork';
  public static readonly CLOSE_CONTAINER_ACTION: string = 'closeContainer';
  public static readonly CANCEL_CASE: string = 'cancelCase';
  public static readonly CREATE_WORK: string = 'createWork';

  constructor() {
    this.broadcastChannel = null;
    this.callback = null;
    this.isReverseCoexistenceCase = false;
    this.broadcastEventToIframe = false;
    this.isDirtyDialogActive = false;
    this.onCreateCallback = null;
    this.openAssignmentParams = null;
  }

  private readonly getGlobalObject = (): GlobalObject => global;

  private readonly getUniqueId = (): number | undefined => this.getGlobalObject().uniqueId;

  private readonly setUniqueId = (): void => {
    this.getGlobalObject().uniqueId = new Date().getTime();
  };

  private readonly initBroadcastChannel = (): void => {
    this.setUniqueId();
    this.broadcastChannel = new BroadcastChannel('coexistence');
    this.broadcastChannel.onmessage = (event) => {
      const { uniqueId } = event.data;
      if (uniqueId !== this.getUniqueId()) {
        return;
      }
      switch (event.data.action) {
        case BroadcastUtils.DISPLAY_DIRTY_POPUP:
          this.displayDirtyPopup(event);
          break;

        case BroadcastUtils.LOAD_PREVIEW: {
          this.loadPreview(event);
          break;
        }

        case BroadcastUtils.UPDATE_COEXISTENCE_DRILLDOWN_CASE: {
          this.updateCoexistenceDrilldownCase(event);
          break;
        }

        case BroadcastUtils.OPEN_ASSIGNMENT_ACTION: {
          this.openAssignment(event);
          break;
        }

        case BroadcastUtils.GET_NEXT_WORK_ACTION: {
          this.getNextWork(event);
          break;
        }

        case BroadcastUtils.OPEN_WORK_BY_HANDLE: {
          this.openWorkByHandle(event);
          break;
        }

        case BroadcastUtils.CLOSE_CONTAINER_ACTION: {
          this.closeContainer(event);
          break;
        }

        case BroadcastUtils.CANCEL_CASE: {
          this.handleCaseCancel();
          break;
        }

        case BroadcastUtils.CREATE_WORK: {
          this.createWork(event);
          break;
        }

        default:
          break;
      }
    };
  };

  private readonly displayDirtyPopup = (event: MessageEvent): void => {
    this.broadcastEventToIframe = event.data.fromInfinity;
    if (!this.isDirtyDialogActive) {
      if (event.data.isFormDirty) {
        PubSubUtils.publish(APP_CONSTANTS.PUB_SUB_EVENTS.EVENT_SHOW_CANCEL_ALERT, {
          isModalAction: true,
          hideDelete: true
        });
        this.isDirtyDialogActive = true;
      } else {
        this.callback?.call(this);
      }
    }
  };

  private readonly handleCaseCancel = (): void => {
    const appChildContainers = getChildContainers(publicConstants.APP.APP);
    const childContext = appChildContainers[appChildContainers.length - 1];
    const childContainerItem = getActiveContainerItemName(childContext);
    const key = getDataObject(childContainerItem)?.caseInfo?.ID;
    if (key && childContainerItem) {
      createC11nEnv({}).getPConnect().getActionsApi().getDescendants(key, childContainerItem);
    }

    // update child cases if both parent and child context holds same item
    if (isParentAndChildContextHoldingSameItem(childContainerItem)) {
      const containerName = getParentContainerItem(childContainerItem);
      if (containerName) {
        createC11nEnv({}).getPConnect().getActionsApi().getDescendants(key, containerName);
      }
    }
  };

  private readonly loadPreview = (event: MessageEvent<any>): void => {
    const { pyID: pzInsKey, caseClassName, uniqueId } = event.data;
    if (uniqueId === this.getUniqueId()) {
      const newC11nEnv = createC11nEnv({});
      newC11nEnv.getPConnect().getActionsApi().showCasePreview(pzInsKey, { caseClassName });
    }
  };

  public readonly navigateToDefaultPage = (): void => {
    const DEFAULT_PAGE_CLASS = 'Data-Portal';
    const DEFAULT_PAGE = 'pyHome';
    const newC11nEnv = createC11nEnv({ isRoot: true });
    const navPages = PCore.getStore()?.getState().data.app?.pyPortal?.pyPrimaryNavPages;
    let navPage;
    if (Array.isArray(navPages) && navPages[0]) {
      navPage = navPages[0];
    }

    const PAGE_CLASS = navPage?.pyClassName || DEFAULT_PAGE_CLASS;
    const PAGE = navPage?.pyRuleName || DEFAULT_PAGE;
    newC11nEnv.getPConnect().getActionsApi().showPage(PAGE, PAGE_CLASS);
  };

  private readonly closeContainer = (event: MessageEvent<any>): void => {
    const { uniqueId } = event.data;
    if (uniqueId !== this.getUniqueId()) {
      return;
    }
    this.navigateToDefaultPage();
  };

  private readonly updateCoexistenceDrilldownCase = (event: MessageEvent<any>): void => {
    const { key, caseClassName, actionApi, caseLabel, uniqueId } = event.data;

    if (uniqueId !== this.getUniqueId()) {
      return;
    }

    /* Update Semantic URL and Update Recents Entry through PATCH /recents call */
    let actionName = actionApi;
    if (actionApi === BroadcastUtils.OPEN_WORK_ITEM) {
      actionName = BroadcastUtils.OPEN_WORK_BY_HANDLE;
    }
    RouteParser.updateSemanticURLAndDocumentTitle({ key, caseClassName, actionName, name: caseLabel });
    // Update Recents
    const newC11nEnv = createC11nEnv({});
    newC11nEnv
      .getPConnect()
      .getActionsApi()
      .updateRecents(caseClassName, key.substring(key.lastIndexOf(' ') + 1), caseLabel, key);
  };

  private readonly openAssignment = (event: MessageEvent<any>): void => {
    const { pzInsKey, caseClassName, uniqueId } = event.data;
    if (uniqueId === this.getUniqueId()) {
      if (this.onCreateCallback && this.openAssignmentParams) {
        if (
          pzInsKey &&
          this.openAssignmentParams[0] &&
          this.openAssignmentParams[0].payload &&
          this.openAssignmentParams[0].payload.data
        ) {
          this.openAssignmentParams[0].payload.data.caseInfo.assignments[0].ID = pzInsKey;
        } else if (
          this.openAssignmentParams[0] &&
          this.openAssignmentParams[0].payload &&
          this.openAssignmentParams[0].payload.data
        ) {
          this.openAssignmentParams[0].payload.data.caseInfo.content.coexistenceAction = 'openWorkByHandle';
        }
        if (pzInsKey) {
          const newC11nEnv = createC11nEnv({});
          newC11nEnv.getPConnect().getActionsApi().openAssignment(pzInsKey, caseClassName, {
            containerName: publicConstants.PRIMARY,
            context: publicConstants.APP.APP
          });
        }
      }
      this.onCreateCallback = null;
      this.openAssignmentParams = null;
    }
  };

  private readonly getNextWork = (event: MessageEvent<any>): void => {
    const { uniqueId } = event.data;
    if (uniqueId === this.getUniqueId()) {
      const acName = `${publicConstants.APP.APP}${APP_CONSTANTS.FORWARD_SLASH_STRING}${publicConstants.PRIMARY}`;
      const newC11nEnv = createC11nEnv({
        meta: {},
        options: {
          context: getActiveContainerItemContext(acName)
        }
      });
      newC11nEnv.getPConnect().getActionsApi().getNextWork();
    }
  };

  private readonly openWorkByHandle = (event: MessageEvent<any>): void => {
    const { key: pzInsKey, caseClassName, uniqueId } = event.data;
    if (uniqueId === this.getUniqueId()) {
      const acName = `${publicConstants.APP.APP}${APP_CONSTANTS.FORWARD_SLASH_STRING}${publicConstants.PRIMARY}`;
      const newC11nEnv = createC11nEnv({
        meta: {},
        options: {
          context: getActiveContainerItemContext(acName)
        }
      });
      newC11nEnv.getPConnect().getActionsApi().openWorkByHandle(pzInsKey, caseClassName);
    }
  };

  private readonly createWork = (event: MessageEvent): void => {
    const { strClassName, uniqueId, strFlowParams, caseInitProperties } = event.data;
    if (uniqueId === this.getUniqueId()) {
      const acName = `${publicConstants.APP.APP}${APP_CONSTANTS.FORWARD_SLASH_STRING}${publicConstants.PRIMARY}`;
      const newC11nEnv = createC11nEnv({
        meta: {},
        options: {
          context: getActiveContainerItemContext(acName)
        }
      });
      const startingFieldParams = caseInitProperties + strFlowParams;
      const paramsFormat = new URLSearchParams(startingFieldParams.slice(1));
      const startingFields = this.setStartingFields(paramsFormat);
      newC11nEnv.getPConnect().getActionsApi().createWork(strClassName, { startingFields });
    }
  };

  private readonly setStartingFields = (fields: URLSearchParams) => {
    const startingFields: { [key: string]: string } = {};

    fields.forEach((value: string, key: string) => {
      if (key) {
        startingFields[key] = value;
      }
    });

    return startingFields;
  };

  private readonly destroyBroadcastChannel = (): void => {
    this.getBroadCastChannel()?.close();
    this.broadcastChannel = null;
  };

  public subscribeEvents = (): void => {
    this.setReverseCoexistenceCaseLoaded(true);
    PubSubUtils.subscribe(publicConstants.PUB_SUB_EVENTS.REVERSE_COEXISTENCE_EVENTS.HANDLE_DISCARD, this.handleDiscard);
    PubSubUtils.subscribe(
      publicConstants.PUB_SUB_EVENTS.REVERSE_COEXISTENCE_EVENTS.CHECK_IS_FORM_DIRTY,
      this.postDirtyPopupMessage
    );
  };

  public unsubscribeEvents = (): void => {
    PubSubUtils.unsubscribeAllWithEventType(
      publicConstants.PUB_SUB_EVENTS.REVERSE_COEXISTENCE_EVENTS.CHECK_IS_FORM_DIRTY
    );
    PubSubUtils.unsubscribeAllWithEventType(publicConstants.PUB_SUB_EVENTS.REVERSE_COEXISTENCE_EVENTS.HANDLE_DISCARD);
    this.setReverseCoexistenceCaseLoaded(false);
    this.isDirtyDialogActive = false;
  };

  public init = (includeEvents = true): void => {
    if (this.broadcastChannel === null) {
      this.initBroadcastChannel();
    }
    if (includeEvents) {
      this.subscribeEvents();
    }
  };

  public destroy = (): void => {
    this.destroyBroadcastChannel();
    this.unsubscribeEvents();
  };

  public setCallBackFunction = (callbackFunction: Function): void => {
    this.callback = callbackFunction;
  };

  public setReverseCoexistenceCaseLoaded = (value: boolean): void => {
    this.isReverseCoexistenceCase = value;
  };

  public setIsDirtyDialogActive = (value: boolean): void => {
    this.isDirtyDialogActive = value;
  };

  public getBroadCastChannel = (): BroadcastChannel | null => this.broadcastChannel;

  public getCallBackFunction = () => {
    return this.callback;
  };

  public isReverseCoexistenceCaseLoaded = (): boolean => this.isReverseCoexistenceCase;

  public postDirtyPopupMessage = ({ callBackFunction }: { callBackFunction: Function }): void => {
    this.broadcastChannel?.postMessage({ action: BroadcastUtils.DIRTY_POPUP_C11N, uniqueId: this.getUniqueId() });
    this.setCallBackFunction(callBackFunction);
  };

  public handleDiscard = (): void => {
    if (this.broadcastEventToIframe) {
      this.broadcastChannel?.postMessage({
        action: BroadcastUtils.HANDLE_INFINITY_DISCARD,
        uniqueId: this.getUniqueId()
      });
      this.broadcastEventToIframe = false;
    } else {
      this.callback?.call(this);
      this.callback = null;
    }
    this.isDirtyDialogActive = false;
  };

  public postCreateCaseMessage = (
    key: string | undefined,
    callback: Function,
    paramsForCallback: Actionout[]
  ): void => {
    if (this.broadcastChannel === null) {
      this.init(false);
    }
    this.broadcastChannel?.postMessage({
      action: BroadcastUtils.OPEN_ASSIGNMENT_MODAL,
      uniqueId: this.getUniqueId(),
      key
    });
    this.broadcastEventToIframe = false;
    this.onCreateCallback = callback;
    this.openAssignmentParams = paramsForCallback;
  };
}

export default new BroadcastUtils();
